<template>
    <Teleport to="body">
        <div v-if="isOpen"
            class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div
                class="bg-white dark:bg-slate-900 w-full max-w-md rounded-[32px] shadow-2xl relative overflow-hidden flex flex-col max-h-[90vh]">

                <!-- Header -->
                <div
                    class="p-6 border-b border-gray-100 dark:border-slate-800 flex items-center justify-between shrink-0">
                    <h2 class="text-xl font-black text-gray-900 dark:text-slate-100">Partnership Request</h2>
                    <Button @click="closeModal" variant="ghost"
                        class="!p-2 -mr-2 rounded-full text-gray-400 hover:text-gray-900 dark:hover:text-white">
                        <X :size="20" />
                    </Button>
                </div>

                <!-- Content -->
                <div class="p-6 overflow-y-auto flex-1">

                    <!-- Loading State -->
                    <div v-if="loading" class="flex flex-col items-center justify-center py-8 space-y-4">
                        <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                        <p class="text-sm font-medium text-gray-500 animate-pulse dark:text-slate-400">{{ statusMessage
                            }}</p>
                    </div>

                    <!-- Error State -->
                    <div v-else-if="error" class="flex flex-col items-center justify-center py-4 space-y-4 text-center">
                        <div
                            class="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center text-red-600 dark:bg-red-900/30 dark:text-red-400">
                            <AlertTriangle :size="24" />
                        </div>
                        <div>
                            <h3 class="font-bold text-gray-900 dark:text-slate-100">Invitation Error</h3>
                            <p class="text-xs text-gray-500 mt-1 dark:text-slate-400">{{ error }}</p>
                        </div>
                        <Button @click="closeModal" variant="secondary" size="sm" class="mt-2">
                            {{ canDismiss ? 'Close' : 'Close' }}
                        </Button>

                        <Button v-if="canDismiss" @click="dismissRequest" variant="ghost"
                            class="text-red-500 hover:text-red-700 hover:bg-red-50 text-xs mt-2 dark:text-red-400 dark:hover:bg-red-900/20">
                            <Trash2 :size="14" class="mr-1" /> Dismiss Request
                        </Button>
                    </div>

                    <!-- Selection State -->
                    <div v-else-if="needsShopSelection" class="space-y-6">
                        <div class="text-center space-y-2">
                            <div
                                class="mx-auto w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
                                <Store :size="24" />
                            </div>
                            <p class="text-sm text-gray-600 dark:text-slate-300">
                                Select which shop should accept this partnership.
                            </p>
                        </div>

                        <div class="space-y-2 max-h-60 overflow-y-auto pr-1">
                            <button v-for="shop in myShops" :key="shop.id" @click="selectedShopId = shop.id"
                                class="w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all relative group"
                                :class="selectedShopId === shop.id
                                    ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 dark:border-indigo-500'
                                    : 'border-gray-100 hover:border-indigo-200 dark:border-slate-700 dark:hover:border-slate-600'">
                                <img v-if="shop.logo_url" :src="shop.logo_url"
                                    class="w-10 h-10 rounded-lg object-cover bg-gray-200 dark:bg-slate-700" />
                                <div v-else
                                    class="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold shrink-0 dark:bg-slate-700 dark:text-slate-300">
                                    {{ shop.name.charAt(0) }}
                                </div>

                                <div class="flex-1 text-left min-w-0">
                                    <h3 class="font-bold text-gray-900 dark:text-slate-100 text-sm truncate">{{
                                        shop.name }}</h3>
                                    <p
                                        class="text-[10px] text-gray-500 dark:text-slate-400 uppercase font-bold tracking-wider">
                                        {{ shop.role }}</p>
                                </div>

                                <div v-if="selectedShopId === shop.id"
                                    class="absolute top-3 right-3 text-indigo-600 dark:text-indigo-400">
                                    <CheckCircle :size="18" />
                                </div>
                            </button>
                        </div>
                    </div>

                    <!-- Success State -->
                    <div v-else class="flex flex-col items-center justify-center py-8 space-y-4 text-center">
                        <div
                            class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center text-green-600 dark:bg-green-900/30 dark:text-green-400">
                            <Check :size="32" />
                        </div>
                        <div>
                            <h3 class="font-bold text-gray-900 text-lg dark:text-slate-100">Partnership Active!</h3>
                            <p class="text-sm text-gray-500 mt-1 dark:text-slate-400">Your network has been updated.</p>
                        </div>
                    </div>

                </div>

                <!-- Footer Actions -->
                <div v-if="needsShopSelection && !loading && !error"
                    class="p-6 border-t border-gray-100 dark:border-slate-800 shrink-0">
                    <Button @click="confirmAcceptance" :disabled="!selectedShopId" variant="primary" block
                        class="py-3 shadow-xl shadow-indigo-600/20">
                        Confirm Partnership
                    </Button>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { AlertTriangle, Check, CheckCircle, Store, X, Trash2 } from 'lucide-vue-next';
import { coreState } from '../stores/core.js';
import { useNetworkStore } from '../stores/useNetworkStore.js';
import { supabase } from '../supabase.js';
import { useToast } from '../composables/useToast.js';
import Button from './shared/Button.vue';

const route = useRoute();
const router = useRouter();
const networkStore = useNetworkStore();
const toast = useToast();

// UI State
const isOpen = ref(false);
const loading = ref(false);
const error = ref(null);
const statusMessage = ref('');
const needsShopSelection = ref(false);
const myShops = ref([]);
const selectedShopId = ref(null);
const currentToken = ref(null);

// Dismiss Logic
const canDismiss = ref(false);
const currentRequestId = ref(null);

// Helper to find token
const findToken = () => {
    let token = route.query.partner_token || sessionStorage.getItem('partner_token') || localStorage.getItem('partner_token');
    if (token) {
        sessionStorage.setItem('partner_token', token);
        return token;
    }
    return null;
};

const checkPartnership = () => {
    const token = findToken();
    if (token && coreState.activeUser?.signupCompleted) {
        if (!isOpen.value || currentToken.value !== token) {
            openModal(token);
        }
    }
};

watch(() => [route.query.partner_token, coreState.activeUser], () => {
    checkPartnership();
}, { immediate: true, deep: true });

const openModal = async (token) => {
    currentToken.value = token;
    isOpen.value = true;
    loading.value = true;
    error.value = null;
    canDismiss.value = false;
    currentRequestId.value = null;
    statusMessage.value = "Verifying invitation...";
    needsShopSelection.value = false;

    // 1. Check Auth
    if (!coreState.activeUser) {
        error.value = "Please log in to accept this partnership.";
        loading.value = false;
        return;
    }

    try {
        // 2. Verified Request Details (Need Requester ID)
        const { data: request, error: reqError } = await supabase
            .from('partnership_requests')
            .select('id, requester_shop_id, partnership_type, requester_shop:shops!requester_shop_id(name)')
            .eq('token', token)
            .single();

        if (reqError || !request) {
            throw new Error("Invalid or expired invitation.");
        }

        currentRequestId.value = request.id;
        const requesterShopName = request.requester_shop?.name || 'Partner';

        // 3. Load Eligible Shops
        const { shops, filteredReason } = await fetchEligibleShops(request.requester_shop_id);
        myShops.value = shops;

        if (shops.length === 0) {
            if (filteredReason === 'already_partnered') {
                error.value = `All your shops are already partnered with ${requesterShopName}.`;
                canDismiss.value = true; // Allow clearing
            } else {
                error.value = "You need to manage an active shop to accept partnerships.";
            }
            loading.value = false;
            return;
        }

        // 4. Pre-select if single shop
        if (shops.length === 1) {
            selectedShopId.value = shops[0].id;
        }

        needsShopSelection.value = true;
        loading.value = false;

    } catch (e) {
        console.error(e);
        error.value = e.message || "Failed to load invitation.";
        loading.value = false;
    }
};

// Returns { shops: [], filteredReason: 'none' | 'already_partnered' | 'no_active_shops' }
const fetchEligibleShops = async (requesterShopId) => {
    const shops = [];
    const userId = coreState.activeUser?.id;
    let hasActiveShops = false;
    let allPartnered = false;
    let potentialShopsCount = 0;

    if (userId && coreState.members) {
        // 1. Identify Candidate Shops (Active Member & Active Shop)
        const candidates = [];
        for (const m of coreState.members.values()) {
            // MUST be active member, Owner/Manager
            if (m.userId === userId && ['Owner', 'Manager'].includes(m.role) && m.status === 'active') {
                const shop = coreState.shops.get(m.shopId);
                // MUST be active shop
                if (shop && shop.status === 'active') {
                    candidates.push({ ...shop, role: m.role });
                }
            }
        }

        potentialShopsCount = candidates.length;
        if (potentialShopsCount > 0) hasActiveShops = true;

        // 2. Filter out Existing Partners
        for (const shop of candidates) {
            // Check for existing relationship with requester
            const { count } = await supabase
                .from('shop_partnerships')
                .select('*', { count: 'exact', head: true })
                .or(`and(source_shop_id.eq.${shop.id},distributor_shop_id.eq.${requesterShopId}),and(source_shop_id.eq.${requesterShopId},distributor_shop_id.eq.${shop.id})`)
                .in('status', ['active', 'suspended']); // Suspended still counts as "linked"

            if (count === 0) {
                shops.push(shop);
            }
        }
    }

    let filteredReason = 'none';
    if (shops.length === 0) {
        if (potentialShopsCount > 0) filteredReason = 'already_partnered';
        else filteredReason = 'no_active_shops';
    }

    return { shops, filteredReason };
};

const confirmAcceptance = async () => {
    if (!selectedShopId.value || !currentToken.value) return;

    needsShopSelection.value = false;
    loading.value = true;
    statusMessage.value = "Accepting partnership...";

    try {
        await networkStore.acceptPartnershipByToken(currentToken.value, selectedShopId.value);

        // Success
        loading.value = false;

        // Close after delay
        setTimeout(() => {
            closeModal();
            // Trigger naive refresh if on network view
            if (route.path === '/network') {
                window.location.reload();
            }
        }, 1200);

    } catch (e) {
        console.error(e);
        let msg = e.message;
        if (msg.includes('Application error:')) {
            msg = msg.replace('Application error:', '').trim();
        }
        error.value = msg || "Failed to accept partnership.";
        loading.value = false;
    }
}

const dismissRequest = async () => {
    if (!currentRequestId.value) return;

    // Optimistic UI
    loading.value = true;
    statusMessage.value = "Dismissing request...";
    error.value = null;

    try {
        // Rejecting the request effectively dismisses it (status -> rejected)
        await networkStore.respondToPartnership(currentRequestId.value, 'rejected');
        toast.showToast("Request dismissed.", "success");
        closeModal();
    } catch (e) {
        console.error("Failed to dismiss:", e);
        error.value = "Failed to dismiss request.";
        loading.value = false;
        canDismiss.value = true; // Restore button
    }
}

const closeModal = () => {
    isOpen.value = false;
    // Clear storage
    sessionStorage.removeItem('partner_token');
    localStorage.removeItem('partner_token');

    // Clear the query param without reloading
    const query = { ...route.query };
    delete query.partner_token;
    router.replace({ query });
};

</script>
